import type { Job } from "@/data/types";
import { computeJobEconomics } from "@/data/revenueIntelligence";
import { useMemo } from "react";
import {
  DollarSign, Package, Wrench, Megaphone, TrendingUp, ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  job: Job;
}

const fmt = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

export function JobEconomicsBreakdown({ job }: Props) {
  const econ = useMemo(() => computeJobEconomics(job), [job]);

  const marginColor = econ.margin >= 0.30 ? "text-success" : econ.margin >= 0.15 ? "text-foreground" : "text-destructive";
  const MarginIcon = econ.margin >= 0.30 ? ArrowUpRight : econ.margin >= 0.15 ? Minus : ArrowDownRight;

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-muted/30">
        <TrendingUp className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">Job Economics</h3>
        <div className={cn("ml-auto flex items-center gap-1 text-sm font-bold", marginColor)}>
          <MarginIcon className="h-4 w-4" />
          {pct(econ.margin)} margin
        </div>
      </div>
      <div className="p-4 space-y-3">
        <Row icon={DollarSign} label="Contract Value" value={fmt(econ.contractValue)} color="text-primary" bold />
        <div className="h-px bg-border" />
        <Row icon={Package} label="Material Cost" value={`-${fmt(econ.materialCost)}`} color="text-muted-foreground" />
        <Row
          icon={Wrench}
          label="Install Cost"
          value={`-${fmt(econ.installCost)}`}
          color="text-muted-foreground"
          sub={[
            "Payout / Labor",
            econ.incentiveCost > 0 ? "Incentives" : "",
            econ.otherExpenseCost > 0 ? "Other expenses" : "",
          ].filter(Boolean).join(" + ")}
        />
        {econ.marketingCost > 0 && (
          <Row icon={Megaphone} label="Marketing Cost" value={`-${fmt(econ.marketingCost)}`} color="text-muted-foreground" sub={job.marketing?.leadSource || ""} />
        )}
        <div className="h-px bg-border" />
        <Row
          icon={TrendingUp}
          label="Net Profit"
          value={fmt(econ.netProfit)}
          color={econ.netProfit >= 0 ? "text-success" : "text-destructive"}
          bold
        />
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, color, bold, sub }: {
  icon: React.ElementType; label: string; value: string; color: string; bold?: boolean; sub?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className={cn("h-3.5 w-3.5", color)} />
        <div>
          <span className={cn("text-sm", bold ? "font-semibold text-foreground" : "text-muted-foreground")}>{label}</span>
          {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
        </div>
      </div>
      <span className={cn("text-sm font-mono", bold ? "font-bold" : "font-medium", color)}>{value}</span>
    </div>
  );
}
